import { GraphQLResult } from "@aws-amplify/api";

import { collectAllPages, parseAwsJson } from "@examspark/common";
import {
  AuthToken,
  IDSK,
  QueryListUserWorkspacesArgs,
  User,
} from "@examspark/types";

import {
  GraphQLAPI,
  fragments,
  PickedWorkspaceUser,
  WorkspaceWithLicense,
} from "..";
import { updateAccount } from "../queries/user";

export const getUser = async (userId: string, params?: { fields?: string }) => {
  const result = await GraphQLAPI.client.query<{
    getUser: Pick<
      User,
      | "acl"
      | "avatarHash"
      | "body"
      | "createdAt"
      | "cognitoState"
      | "data"
      | "email"
      | "eventId"
      | "firstName"
      | "id"
      | "intercomId"
      | "jobId"
      | "lastActiveAt"
      | "lastName"
      | "sk"
      | "status"
      | "title"
      | "type"
      | "updatedAt"
      | "userId"
      | "workspaceId"
    >;
  }>({
    query: /* GraphQL */ `
      query GetUser($input: ModelGetInput) {
        getUser(input: $input) {
          acl
          avatarHash
          body
          createdAt
          data
          email
          eventId
          firstName
          id
          intercomId
          jobId
          lastActiveAt
          lastName
          sk
          status
          title
          type
          updatedAt
          userId
          workspaceId
          ${params?.fields ?? ""}
        }
      }
    `,
    variables: {
      input: {
        id: userId,
        sk: "user",
      },
    },
  });

  return result.data?.getUser;
};

export const updateUserAccount = async (data: Partial<User>) => {
  const result = await GraphQLAPI.client.query<{ updateAccount: User }>({
    query: updateAccount(),
    variables: {
      input: data,
    },
  });

  return result;
};

export const setUserCognitoStateConfirmed = async (data: {
  username: string;
}) => {
  const result = await GraphQLAPI.client.query<{
    setUserConfirmed: IDSK;
  }>({
    query: /* GraphQL */ `
      mutation ConfirmUser($input: LoginUserInput!) {
        setUserConfirmed(input: $input) {
          id
          sk
        }
      }
    `,
    variables: {
      input: data,
    },
  });

  return result;
};

export const loginAsUser = async (username: string) => {
  const results = await GraphQLAPI.client.query<{
    authGetToken: AuthToken;
  }>({
    query: /* GraphQL */ `
      query AuthGetToken($input: AuthTokenInput) {
        authGetToken(input: $input) {
          AccessToken
          IdToken
          RefreshToken
          ClientId
        }
      }
    `,
    variables: {
      input: {
        username,
      },
    },
  });

  return results.data?.authGetToken;
};

export const getLoginTokenForAppUser = async (params: {
  appKey: string;
  appId: string;
  userId: string;
  workspaceId?: string;
}) => {
  const results = await GraphQLAPI.query<{
    getLoginTokenForAppUser: string;
  }>({
    query: /* GraphQL */ `
      query GetLoginTokenForAppUser($input: ControllerInput!) {
        getLoginTokenForAppUser(input: $input)
      }
    `,
    variables: {
      input: {
        action: "getLoginTokenForAppUser",
        params: JSON.stringify(params),
      },
    },
  });

  if (results.errors) {
    console.error(results.errors);
    return { error: results.errors?.[0].message };
  }

  return parseAwsJson<{ url: string; error: undefined }>(
    results.data?.getLoginTokenForAppUser,
  );
};

export const adminLoginAsAppUser = async (params: {
  appKey: string;
  appId: string;
  userId: string;
  workspaceId?: string;
}): Promise<{ url?: string; error?: string }> => {
  const results = await GraphQLAPI.query<{
    adminLoginAsAppUser: string;
  }>({
    query: /* GraphQL */ `
      query AdminLoginAsAppUser($input: ControllerInput!) {
        adminLoginAsAppUser(input: $input)
      }
    `,
    variables: {
      input: {
        action: "adminLoginAsAppUser",
        params: JSON.stringify(params),
      },
    },
  });

  if (results.errors) {
    console.error(results.errors);
    return { error: results.errors?.[0].message };
  }

  const result = parseAwsJson<{ url: string; error: undefined }>(
    results.data?.adminLoginAsAppUser,
  );

  if (typeof result === "undefined") {
    return { error: "Could not parse adminLoginAsAppUser response" };
  }

  return result;
};

export const fetchUserWorkspaces = async (params: {
  userId?: string;
  appId?: string;
  enabled?: boolean;
  primaryOnly?: boolean;
}) => {
  const pages = await collectAllPages(async (nextToken) => {
    return (
      (await GraphQLAPI.client.query({
        query: /* GraphQL */ `
            query ListUserWorkspaces($input: ListUserWorkspacesInput, $authorizationContext: AuthorizationContextInput, $nextToken: String) {
              listUserWorkspaces(input: $input, authorizationContext: $authorizationContext, nextToken: $nextToken) {
                items {
                  ${fragments.workspace.listUserWorkspaceFields}
                }
                nextToken
              }
            }
          `,
        variables: {
          input: {
            primaryOnly: params.primaryOnly,
            userId: params.userId,
          },
          nextToken,
        } as QueryListUserWorkspacesArgs,
      })) as GraphQLResult<{
        listUserWorkspaces: {
          items: PickedWorkspaceUser[];
          nextToken?: string;
        };
      }>
    ).data?.listUserWorkspaces;
  });

  return pages.flatMap((page) => page.items ?? []);
};

export const listUserWorkspaces = async (params: {
  userId?: string;
  appId?: string;
  enabled?: boolean;
  primaryOnly?: boolean;
}) => {
  // console.log("Listing user workspaces", params.appId, params.userId);
  const pages = await fetchUserWorkspaces(params);

  // console.log("Found user workspaces", workspaces);
  const result =
    pages
      .map((w) => ({
        ...w,
        appLicense: w.appLicenses?.find((l) => l.appId === params.appId),
      }))
      .filter(
        (wa) =>
          (wa.appLicense != null && wa.appLicense.isDisabled !== true) ||
          params.appId == null,
      ) ?? [];

  // console.log("result", result);
  return result as WorkspaceWithLicense[];
};

/**
 * Lists all user workspaces, and binds the FIRST application that workspace
 * owns (workspaces for now typically only license one app) to the `appLicense`
 * prop. This list is then displayed in Campfire NEO's workspace selector and
 * tries to launch NEO as that workspace ID. */
export const listUserAppsByWorkspace = async (params: {
  userId?: string;
  enabled?: boolean;
  primaryOnly?: boolean;
}) => {
  console.log("Listing user workspaces", params.userId);
  const pages = await fetchUserWorkspaces(params);

  console.log("Found user workspaces", pages);
  const result =
    pages
      .map((w) => ({
        ...w,
      }))
      .filter((w) => !!w.appLicenses?.length) ?? [];

  console.log("all user workspaces", result);
  return result as PickedWorkspaceUser[];
};
